import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/helpers';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | 'sm' | 'md';
}

const PADDING = { none: '', sm: 'p-4', md: 'p-5 sm:p-6' };

export const Card = forwardRef<HTMLDivElement, CardProps>(({ className, padding = 'md', ...props }, ref) => (
  <div
    ref={ref}
    className={cn('rounded-xl border border-secondary-200 bg-white shadow-sm', PADDING[padding], className)}
    {...props}
  />
));

Card.displayName = 'Card';

interface CardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function CardHeader({ title, subtitle, action, className }: CardHeaderProps) {
  return (
    <div className={cn('mb-4 flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-secondary-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-secondary-500">{subtitle}</p>}
      </div>
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-secondary-900">{title}</h1>
        {subtitle && <p className="mt-1 text-secondary-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      {icon && <div className="mb-3 text-secondary-300">{icon}</div>}
      <p className="font-medium text-secondary-700">{title}</p>
      {children && <div className="mt-1 text-sm text-secondary-500">{children}</div>}
    </div>
  );
}

export function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-secondary-500">{label}</dt>
      <dd className="mt-1 text-sm text-secondary-900">{children}</dd>
    </div>
  );
}
